import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, Check } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const ProductPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${slug}`);
      setProduct(response.data);
      setSelectedSize(response.data.sizes[0] || '');
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    addToCart(product, selectedSize, quantity);
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="product-loading">
        <div className="w-12 h-12 border-4 border-brand-pearl/20 border-t-brand-pearl rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-brand-pearl mb-4">Product Not Found</h2>
          <Link to="/shop" className="text-brand-walnut hover:text-brand-pearl">Back to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="product-page">
      <section className="section-spacing">
        <div className="container-custom">
          {/* Back Button */}
          <Link
            to="/shop"
            data-testid="back-to-shop-btn"
            className="inline-flex items-center text-brand-walnut hover:text-brand-pearl mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="sticky top-24">
                <div className="parallax-img rounded-sm overflow-hidden border border-brand-pearl/10">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-auto object-cover"
                    data-testid="product-image"
                  />
                </div>
              </div>
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-4">
                <span className="text-xs font-mono uppercase tracking-widest text-brand-walnut" data-testid="product-category">
                  {product.category}
                </span>
              </div>
              <h1 className="text-4xl font-heading font-bold text-brand-pearl mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-3xl font-bold text-brand-pearl mb-6" data-testid="product-price">
                ₹{product.price.toLocaleString()}
              </p>

              <div className="border-t border-b border-brand-pearl/10 py-6 mb-6">
                <p className="text-brand-walnut leading-relaxed" data-testid="product-description">
                  {product.description}
                </p>
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-brand-pearl mb-3">Select Size</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      data-testid={`size-btn-${size}`}
                      className={`w-14 h-14 flex items-center justify-center border-2 rounded-sm font-medium transition-colors ${
                        selectedSize === size
                          ? 'border-brand-pearl bg-brand-pearl text-brand-ivory'
                          : 'border-brand-pearl/20 text-brand-pearl hover:border-brand-pearl'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-brand-pearl mb-3">Quantity</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="quantity-decrease"
                    className="w-10 h-10 flex items-center justify-center border border-brand-pearl/20 rounded-sm hover:bg-brand-timberwolf transition-colors"
                  >
                    -
                  </button>
                  <span className="text-lg font-medium text-brand-pearl" data-testid="quantity-display">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="quantity-increase"
                    className="w-10 h-10 flex items-center justify-center border border-brand-pearl/20 rounded-sm hover:bg-brand-timberwolf transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <button
                onClick={handleAddToCart}
                disabled={!product.in_stock}
                data-testid="add-to-cart-btn"
                className="w-full flex items-center justify-center px-8 py-4 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="mr-2 w-5 h-5" />
                {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
              </button>

              {/* Features */}
              <div className="mt-8 space-y-3">
                <div className="flex items-center text-sm text-brand-walnut">
                  <Check className="w-4 h-4 mr-2 text-brand-green" />
                  Free shipping on orders over ₹2,000
                </div>
                <div className="flex items-center text-sm text-brand-walnut">
                  <Check className="w-4 h-4 mr-2 text-brand-green" />
                  7-day return policy
                </div>
                <div className="flex items-center text-sm text-brand-walnut">
                  <Check className="w-4 h-4 mr-2 text-brand-green" />
                  Premium quality fabrics
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;